import React, { useState } from "react";
import { leadsApi } from "../services/api"; // ⚠️ adjust path to wherever api.js lives in your project
import WhatsAppComposer from "../components/Whatsappcomposer";
import WhatsAppChatView from "../components/WhatsAppChatView"; // ✅ NEW

/**
 * WhatsAppPage
 * ─────────────────────────────────────────────────────────────────
 * Search a lead → select it → see chat history (WhatsAppChatView)
 * above the message composer (WhatsAppComposer). Sending a message
 * bumps `sendCount`, which the chat view watches to refetch instantly
 * instead of waiting for its next 5s poll.
 * ─────────────────────────────────────────────────────────────────
 */
export default function WhatsAppPage() {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [sendCount, setSendCount] = useState(0); // ✅ NEW — bumped after each successful send

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;

    setSearching(true);
    try {
      const data = await leadsApi.searchLeads(query.trim());
      const list = Array.isArray(data) ? data : data?.content || data?.data || [];
      setResults(list);
    } catch (err) {
      console.error("Lead search failed:", err);
      setResults([]);
    } finally {
      setSearching(false);
    }
  };

  const selectedPhone = selectedLead?.contactNumber || selectedLead?.phoneNumber || "";

  return (
    <div style={styles.page}>
      <h2 style={styles.pageTitle}>WhatsApp</h2>

      <div style={styles.layout}>
        {/* ── Left: lead search + result list ── */}
        <div style={styles.leftPanel}>
          <form onSubmit={handleSearch} style={styles.searchRow}>
            <input
              style={styles.searchInput}
              type="text"
              placeholder="Search lead by name or phone..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <button type="submit" style={styles.searchButton} disabled={searching}>
              {searching ? "..." : "Search"}
            </button>
          </form>

          <div style={styles.resultsList}>
            {results.length === 0 && !searching && (
              <div style={styles.emptyState}>Search for a lead to begin.</div>
            )}
            {results.map((lead) => (
              <div
                key={lead.id}
                style={{
                  ...styles.resultItem,
                  ...(selectedLead?.id === lead.id ? styles.resultItemActive : {}),
                }}
                onClick={() => setSelectedLead(lead)}
              >
                <div style={styles.resultName}>{lead.customerName || lead.name || lead.fullName || "Unnamed"}</div>
                <div style={styles.resultPhone}>{lead.contactNumber || lead.phoneNumber || "-"}</div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Right: chat history + composer for selected lead ── */}
        <div style={styles.rightPanel}>
          {selectedLead ? (
            <>
              <div style={styles.selectedHeader}>
                Sending to: <strong>{selectedLead.customerName || selectedLead.name || selectedLead.fullName || "Customer"}</strong> ({selectedPhone})
              </div>

              {/* ✅ Chat history — shown above the composer */}
              <div style={styles.chatWrapper}>
                <WhatsAppChatView phoneNumber={selectedPhone} refreshKey={sendCount} />
              </div>

              {/* Composer — bumps sendCount on success so the chat view refetches immediately */}
              <WhatsAppComposer
                key={selectedLead.id}
                defaultTo={selectedPhone}
                onSent={(data) => {
                  console.log("WhatsApp sent:", data);
                  setSendCount((c) => c + 1);
                }}
              />
            </>
          ) : (
            <div style={styles.emptyState}>Select a lead from the left to send a message.</div>
          )}
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: { padding: 24, fontFamily: "Inter, system-ui, sans-serif" },
  pageTitle: { fontSize: 20, fontWeight: 700, marginBottom: 16, color: "#1e293b" },
  layout: { display: "flex", gap: 20, alignItems: "flex-start" },
  leftPanel: { width: 320, border: "1px solid #e2e8f0", borderRadius: 12, background: "#fff", overflow: "hidden" },
  searchRow: { display: "flex", gap: 8, padding: 14, borderBottom: "1px solid #e2e8f0" },
  searchInput: { flex: 1, padding: "8px 10px", fontSize: 13.5, borderRadius: 8, border: "1px solid #cbd5e1", outline: "none" },
  searchButton: { padding: "8px 14px", fontSize: 13.5, fontWeight: 600, color: "#fff", background: "#1e293b", border: "none", borderRadius: 8, cursor: "pointer" },
  resultsList: { maxHeight: 480, overflowY: "auto" },
  resultItem: { padding: "10px 14px", cursor: "pointer", borderBottom: "1px solid #f1f5f9" },
  resultItemActive: { background: "#ecfdf5" },
  resultName: { fontSize: 13.5, fontWeight: 600, color: "#1e293b" },
  resultPhone: { fontSize: 12, color: "#64748b", marginTop: 2 },
  rightPanel: { flex: 1, display: "flex", flexDirection: "column", gap: 16 },
  chatWrapper: {}, // WhatsAppChatView has its own sizing/border, no wrapper styling needed
  selectedHeader: { fontSize: 13.5, color: "#475569" },
  emptyState: { padding: 24, textAlign: "center", color: "#94a3b8", fontSize: 13.5 },
};